import "./tailwind.css";
import { useEffect, useState, useRef } from "react";
import { usePageContext } from "vike-react/usePageContext";
import { langCodeToName, applyTheme } from "../utils/appTheme.js";
import { useProgressSync } from "../utils/useProgressSync.js";
import { useGardenSync } from "../utils/useGardenSync.js";

// ── Design tokens (CSS var references) ─────────────────────────────────────
const C = {
  bg: 'var(--bg)', surf: 'var(--surf)', surf2: 'var(--surf2)',
  border: 'var(--border)', text: 'var(--text)', muted: 'var(--muted)',
  dim: 'var(--dim)', accent: 'var(--accent)', accentBg: 'var(--accent-bg)',
  accentBorder: 'var(--accent-border)',
};
const AF = {
  h: "'Space Grotesk', system-ui, sans-serif",
  b: "'DM Sans', system-ui, sans-serif",
  m: "'DM Mono', 'Courier New', monospace",
};

// ── Icons ───────────────────────────────────────────────────────────────────
function IconDashboard({ size = 16, color = 'currentColor' }: { size?: number; color?: string }) {
  return <svg width={size} height={size} viewBox="0 0 16 16" fill="none"><rect x="1" y="1" width="6" height="6" rx="1.5" fill={color}/><rect x="9" y="1" width="6" height="6" rx="1.5" fill={color}/><rect x="1" y="9" width="6" height="6" rx="1.5" fill={color}/><rect x="9" y="9" width="6" height="6" rx="1.5" fill={color}/></svg>;
}
function IconPractice({ size = 16, color = 'currentColor' }: { size?: number; color?: string }) {
  return <svg width={size} height={size} viewBox="0 0 16 16" fill="none"><path d="M5 3.5l7 4.5-7 4.5V3.5z" fill={color}/></svg>;
}
function IconProgress({ size = 16, color = 'currentColor' }: { size?: number; color?: string }) {
  return <svg width={size} height={size} viewBox="0 0 16 16" fill="none"><rect x="1" y="9" width="3" height="6" rx="1" fill={color}/><rect x="6.5" y="5.5" width="3" height="9.5" rx="1" fill={color}/><rect x="12" y="2" width="3" height="13" rx="1" fill={color}/></svg>;
}
function IconSettings({ size = 16, color = 'currentColor' }: { size?: number; color?: string }) {
  return <svg width={size} height={size} viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="2.5" stroke={color} strokeWidth="1.5"/><path d="M8 1.5v2M8 12.5v2M1.5 8h2M12.5 8h2M3.4 3.4l1.42 1.42M11.18 11.18l1.42 1.42M3.4 12.6l1.42-1.42M11.18 4.82l1.42-1.42" stroke={color} strokeWidth="1.5" strokeLinecap="round"/></svg>;
}
function IconGarden({ size = 16, color = 'currentColor' }: { size?: number; color?: string }) {
  return <svg width={size} height={size} viewBox="0 0 16 16" fill="none"><path d="M8 14V7" stroke={color} strokeWidth="1.5" strokeLinecap="round"/><path d="M8 8.5C8 6.5 6.2 5 4.2 5C4.2 7 5.8 8.5 8 8.5Z" fill={color} opacity="0.55"/><path d="M8 7.5C8 5.5 9.8 4 11.8 4C11.8 6 10.2 7.5 8 7.5Z" fill={color}/><path d="M3.5 14h9" stroke={color} strokeWidth="1.5" strokeLinecap="round" opacity="0.5"/></svg>;
}
function IconReading({ size = 16, color = 'currentColor' }: { size?: number; color?: string }) {
  return <svg width={size} height={size} viewBox="0 0 16 16" fill="none"><path d="M2 2.5C2 2.5 4.5 2 8 3.5C11.5 2 14 2.5 14 2.5V13C14 13 11.5 12.5 8 14C4.5 12.5 2 13 2 13V2.5Z" stroke={color} strokeWidth="1.4" strokeLinejoin="round"/><path d="M8 3.5V14" stroke={color} strokeWidth="1.4"/></svg>;
}
function IconSpeaking({ size = 16, color = 'currentColor' }: { size?: number; color?: string }) {
  return <svg width={size} height={size} viewBox="0 0 16 16" fill="none"><rect x="5" y="1.5" width="6" height="8" rx="3" stroke={color} strokeWidth="1.4"/><path d="M2.5 8.5C2.5 11.26 4.97 13.5 8 13.5C11.03 13.5 13.5 11.26 13.5 8.5" stroke={color} strokeWidth="1.4" strokeLinecap="round"/><line x1="8" y1="13.5" x2="8" y2="15" stroke={color} strokeWidth="1.4" strokeLinecap="round"/></svg>;
}
function IconGrammar({ size = 16, color = 'currentColor' }: { size?: number; color?: string }) {
  return <svg width={size} height={size} viewBox="0 0 16 16" fill="none"><path d="M2 13.5L5.8 3h.8l3.8 10.5" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><path d="M3.4 9.5h5" stroke={color} strokeWidth="1.3" strokeLinecap="round"/><path d="M11.5 6.5v7" stroke={color} strokeWidth="1.4" strokeLinecap="round" opacity="0.5"/><path d="M9.5 8.5h4" stroke={color} strokeWidth="1.4" strokeLinecap="round" opacity="0.5"/></svg>;
}
function IconVocabulary({ size = 16, color = 'currentColor' }: { size?: number; color?: string }) {
  return <svg width={size} height={size} viewBox="0 0 16 16" fill="none"><rect x="2" y="3.5" width="12" height="9" rx="1.5" stroke={color} strokeWidth="1.5"/><line x1="5" y1="8" x2="11" y2="8" stroke={color} strokeWidth="1.4" strokeLinecap="round"/><line x1="5" y1="10.5" x2="9" y2="10.5" stroke={color} strokeWidth="1.1" strokeLinecap="round" opacity="0.55"/></svg>;
}
function IconSentences({ size = 16, color = 'currentColor' }: { size?: number; color?: string }) {
  return <svg width={size} height={size} viewBox="0 0 16 16" fill="none"><line x1="2" y1="4" x2="14" y2="4" stroke={color} strokeWidth="1.5" strokeLinecap="round"/><line x1="2" y1="8" x2="12" y2="8" stroke={color} strokeWidth="1.5" strokeLinecap="round"/><line x1="2" y1="12" x2="8.5" y2="12" stroke={color} strokeWidth="1.5" strokeLinecap="round"/></svg>;
}
function IconQuickPractice({ size = 16, color = 'currentColor' }: { size?: number; color?: string }) {
  return <svg width={size} height={size} viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="5.5" stroke={color} strokeWidth="1.5"/><path d="M8 5v3.5l2.5 1.8" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>;
}

function Kbd({ children }: { children: string }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: AF.m, fontSize: 10, color: C.muted,
      background: C.surf2, border: `1px solid ${C.border}`,
      borderBottomWidth: 2, padding: '1px 5px', borderRadius: 4,
      lineHeight: 1.5, userSelect: 'none', whiteSpace: 'nowrap', flexShrink: 0,
    }}>{children}</span>
  );
}

// ── Language dropdown ────────────────────────────────────────────────────────
function LangDropdown({ langCode, onChange }: { langCode: string; onChange: (code: string) => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const langs = [
    { code: 'es', flag: '🇪🇸', label: 'Español',   note: 'España · Latinoamérica', available: true  },
    { code: 'pt', flag: '🇧🇷', label: 'Português', note: 'Brasil',                  available: true  },
    { code: 'fi', flag: '🇫🇮', label: 'Suomi',     note: 'Finland',                 available: true  },
    { code: 'it', flag: '🇮🇹', label: 'Italiano',  note: 'Italia',                  available: false },
    { code: 'fr', flag: '🇫🇷', label: 'Français',  note: 'France',                  available: false },
    { code: 'ca', flag: '🏴',  label: 'Català',    note: 'Catalunya',               available: false },
  ];
  const curr = langs.find(l => l.code === langCode && l.available) || langs[0];

  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  return (
    <div ref={ref} style={{ position: 'relative', padding: '0 12px 12px' }}>
      <div style={{ fontFamily: AF.m, fontSize: 9, color: C.dim, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 6 }}>Learning</div>
      <button onClick={() => setOpen(o => !o)} style={{
        width: '100%', display: 'flex', alignItems: 'center', gap: 9,
        padding: '10px 12px', borderRadius: 7,
        background: C.surf, border: `1px solid ${C.border}`,
        cursor: 'pointer',
      }}>
        <span style={{ fontSize: 17 }}>{curr.flag}</span>
        <div style={{ flex: 1, textAlign: 'left' }}>
          <div style={{ fontFamily: AF.h, fontSize: 14, fontWeight: 600, color: C.text, lineHeight: 1.2 }}>{curr.label}</div>
          <div style={{ fontFamily: AF.b, fontSize: 11, color: C.muted, lineHeight: 1.3 }}>{curr.note}</div>
        </div>
        <span style={{ fontFamily: AF.m, fontSize: 10, color: C.muted, transition: 'transform 0.2s', transform: open ? 'rotate(180deg)' : 'none', display: 'block' }}>▾</span>
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% - 4px)', left: 12, right: 12,
          background: C.surf, border: `1px solid ${C.border}`, borderRadius: 7,
          overflow: 'hidden', zIndex: 200, boxShadow: '0 12px 32px rgba(0,0,0,0.45)',
        }}>
          {langs.map(l => {
            const active = langCode === l.code;
            if (!l.available) {
              return (
                <div key={l.code} style={{
                  display: 'flex', alignItems: 'center', gap: 9,
                  padding: '10px 12px',
                  borderLeft: '2px solid transparent',
                  borderBottom: `1px solid ${C.border}`,
                  opacity: 0.5, cursor: 'default',
                }}>
                  <span style={{ fontSize: 16 }}>{l.flag}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontFamily: AF.h, fontSize: 13, color: C.muted }}>{l.label}</div>
                    <div style={{ fontFamily: AF.b, fontSize: 11, color: C.dim }}>{l.note}</div>
                  </div>
                  <span style={{
                    fontFamily: AF.m, fontSize: 9, color: C.dim,
                    border: `1px solid ${C.border}`, borderRadius: 4,
                    padding: '2px 6px', letterSpacing: '0.06em', textTransform: 'uppercase',
                    whiteSpace: 'nowrap',
                  }}>Soon</span>
                </div>
              );
            }
            return (
              <button key={l.code} onClick={() => { onChange(l.code); setOpen(false); }} style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: 9,
                padding: '10px 12px', background: 'transparent',
                borderLeft: `2px solid ${active ? 'var(--accent)' : 'transparent'}`,
                borderTop: 'none', borderRight: 'none',
                borderBottom: `1px solid ${C.border}`,
                cursor: 'pointer',
              }}>
                <span style={{ fontSize: 16 }}>{l.flag}</span>
                <div style={{ flex: 1, textAlign: 'left' }}>
                  <div style={{ fontFamily: AF.h, fontSize: 13, fontWeight: active ? 600 : 400, color: active ? 'var(--accent)' : C.text }}>{l.label}</div>
                  <div style={{ fontFamily: AF.b, fontSize: 11, color: C.muted }}>{l.note}</div>
                </div>
                {active && <span style={{ color: 'var(--accent)', fontSize: 12, fontWeight: 700 }}>✓</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────────────
function langCodeToUrlName(code: string): string {
  const map: Record<string, string> = { es: 'spanish', pt: 'portuguese', fi: 'finnish', fr: 'french', it: 'italian', ca: 'catalan', ro: 'romanian' };
  return map[code] ?? 'spanish';
}

function getActiveScreen(pathname: string): string {
  if (pathname.match(/\/(learn-[^/]+\/)?(progress)(\/?$)/)) return 'progress';
  if (pathname.match(/\/(learn-[^/]+\/)?(settings)(\/?$)/)) return 'settings';
  if (pathname.match(/\/(learn-[^/]+\/)?(pricing)(\/?$)/)) return 'pricing';
  if (pathname.match(/\/(learn-[^/]+\/)?grammar(\/?|\/.*)?$/)) return 'grammar';
  if (pathname.match(/\/(learn-[^/]+\/)?vocabulary(\/?|\/.*)?$/)) return 'vocab';
  if (pathname.match(/\/(learn-[^/]+\/)?sentences(\/?|\/.*)?$/)) return 'sentences';
  if (pathname.match(/\/(learn-[^/]+\/)?(practice|quiz)(\/?|\/.*)?$/)) return 'quiz';
  if (pathname.match(/\/(learn-[^/]+\/)?comprehension(\/?|\/.*)?$/)) return 'reading';
  if (pathname.match(/\/(learn-[^/]+\/)?speaking(\/?|\/.*)?$/)) return 'speaking';
  if (pathname.match(/\/(learn-[^/]+\/)?garden(\/?|\/.*)?$/)) return 'garden';
  return 'dashboard';
}


// ── Desktop Sidebar ──────────────────────────────────────────────────────────
function AppSidebar({ langCode, pathname, onLanguageChange }: {
  langCode: string;
  pathname: string;
  onLanguageChange: (code: string) => void;
}) {
  const active = getActiveScreen(pathname);
  const prefix = `/learn-${langCodeToUrlName(langCode)}`;

  const topItems = [
    { id: 'dashboard', label: 'Dashboard', Icon: IconDashboard, key: 'D', href: prefix },
  ];
  const trainingItems = [
    { id: 'grammar',   label: 'Grammar',         Icon: IconGrammar,       href: `${prefix}/grammar` },
    { id: 'vocab',     label: 'Vocabulary',       Icon: IconVocabulary,    href: `${prefix}/vocabulary` },
    { id: 'sentences', label: 'Sentences',        Icon: IconSentences,     href: `${prefix}/sentences` },
    { id: 'quiz',      label: 'Quick Practice',   Icon: IconQuickPractice, href: `${prefix}/practice` },
    { id: 'reading',   label: 'Reading',          Icon: IconReading,       href: `${prefix}/comprehension` },
    { id: 'speaking',  label: 'Speaking',         Icon: IconSpeaking,      href: `${prefix}/speaking` },
  ];
  const bottomItems = [
    { id: 'progress', label: 'Progress', Icon: IconProgress, key: 'R', href: `${prefix}/progress` },
    { id: 'settings', label: 'Settings', Icon: IconSettings, key: 'S', href: `${prefix}/settings` },
  ];

  const navItemStyle = (isActive: boolean) => ({
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '10px 20px', textDecoration: 'none',
    background: isActive ? C.accentBg : 'transparent',
    borderLeft: `2px solid ${isActive ? 'var(--accent)' : 'transparent'}`,
    color: isActive ? 'var(--accent)' : C.muted,
    fontFamily: AF.b, fontSize: 15, fontWeight: isActive ? 600 : 400,
    transition: 'all 0.15s',
  } as React.CSSProperties);

  const sectionLabel = {
    padding: '12px 20px 4px',
    fontFamily: AF.m, fontSize: 9, color: C.dim,
    letterSpacing: '0.14em', textTransform: 'uppercase' as const,
  };

  return (
    <div className="c8-sidebar">
      {/* Logo */}
      <div style={{ padding: '20px 20px 14px' }}>
        <a href={prefix} style={{ textDecoration: 'none' }}>
          <span style={{ fontFamily: AF.h, fontSize: 20, fontWeight: 700, color: C.text, letterSpacing: '-0.5px' }}>
            Conjugr<span style={{ color: 'var(--accent)' }}>8</span>
          </span>
        </a>
      </div>
      {/* Language selector */}
      <LangDropdown langCode={langCode} onChange={onLanguageChange}/>
      <div style={{ height: 1, background: C.border, margin: '0 0 8px' }}/>
      {/* Nav */}
      <nav style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Dashboard */}
        {topItems.map(({ id, label, Icon, key, href }) => {
          const isActive = active === id;
          return (
            <a key={id} href={href} style={navItemStyle(isActive)}>
              <Icon size={16} color={isActive ? 'var(--accent)' : C.muted}/>
              <span style={{ flex: 1 }}>{label}</span>
              <Kbd>{key}</Kbd>
            </a>
          );
        })}

        {/* Conjugarden — the heart of the app */}
        <a href={`${prefix}/garden`} style={{
          display: 'flex', alignItems: 'center', gap: 11, margin: '8px 12px 4px', padding: '12px 14px',
          textDecoration: 'none', borderRadius: 10,
          border: `1px solid ${active === 'garden' ? 'var(--accent)' : C.accentBorder}`,
          background: C.accentBg,
        }}>
          <IconGarden size={18} color={'var(--accent)'}/>
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: AF.h, fontSize: 15, fontWeight: 700, color: 'var(--accent)', letterSpacing: '-0.3px' }}>Conjugarden</div>
            <div style={{ fontFamily: AF.b, fontSize: 11, color: C.muted }}>Expeditions await</div>
          </div>
        </a>

        {/* Training section */}
        <div style={{ ...sectionLabel, paddingBottom: 0 }}>Training</div>
        <div style={{ padding: '0 20px 6px', fontFamily: AF.b, fontSize: 10.5, color: C.dim }}>Sharpen skills for your expeditions</div>
        {trainingItems.map(({ id, label, Icon, href }) => {
          const isActive = active === id;
          return (
            <a key={id} href={href} style={navItemStyle(isActive)}>
              <Icon size={16} color={isActive ? 'var(--accent)' : C.muted}/>
              <span style={{ flex: 1 }}>{label}</span>
            </a>
          );
        })}

        {/* Divider before Progress/Settings */}
        <div style={{ height: 1, background: C.border, margin: '8px 0' }}/>

        {/* Progress, Settings */}
        {bottomItems.map(({ id, label, Icon, key, href }) => {
          const isActive = active === id;
          return (
            <a key={id} href={href} style={navItemStyle(isActive)}>
              <Icon size={16} color={isActive ? 'var(--accent)' : C.muted}/>
              <span style={{ flex: 1 }}>{label}</span>
              <Kbd>{key}</Kbd>
            </a>
          );
        })}
      </nav>
      {/* Upgrade panel */}
      <div style={{ margin: '8px 12px 14px', padding: '12px 14px', borderRadius: 8, border: `1px solid ${C.accentBorder}`, background: C.accentBg }}>
        <div style={{ fontFamily: AF.h, fontSize: 12, fontWeight: 700, color: 'var(--accent)', marginBottom: 2 }}>Free plan</div>
        <div style={{ fontFamily: AF.b, fontSize: 11, color: C.muted, marginBottom: 10 }}>50 verbs / month</div>
        <a href={`${prefix}/pricing`} style={{
          display: 'block', width: '100%', padding: '7px', borderRadius: 5, border: 'none', cursor: 'pointer',
          background: 'var(--accent)', color: C.bg, fontFamily: AF.b, fontSize: 12, fontWeight: 700,
          textAlign: 'center', textDecoration: 'none',
        }}>Upgrade to Pro</a>
      </div>
    </div>
  );
}

// ── Mobile Header ────────────────────────────────────────────────────────────
function MobileHeader({ langCode, onLanguageChange }: {
  langCode: string;
  onLanguageChange: (code: string) => void;
}) {
  const prefix = `/learn-${langCodeToUrlName(langCode)}`;
  return (
    <div className="c8-mobile-header">
      <a href={prefix} style={{ textDecoration: 'none' }}>
        <span style={{ fontFamily: AF.h, fontSize: 17, fontWeight: 700, color: C.text, letterSpacing: '-0.4px' }}>
          Conjugr<span style={{ color: 'var(--accent)' }}>8</span>
        </span>
      </a>
      <div style={{ display: 'flex', gap: 3, background: C.surf, border: `1px solid ${C.border}`, borderRadius: 7, padding: 3 }}>
        {[['es','🇪🇸','ES'],['pt','🇧🇷','PT']].map(([code,flag,label]) => (
          <button key={code} onClick={() => onLanguageChange(code)} style={{
            padding: '6px 10px', borderRadius: 5, border: 'none', cursor: 'pointer',
            background: langCode === code ? 'var(--accent)' : 'transparent',
            color: langCode === code ? C.bg : C.muted,
            fontFamily: AF.b, fontSize: 12, fontWeight: langCode === code ? 700 : 400,
            display: 'flex', alignItems: 'center', gap: 4,
          }}>
            {flag} {label}
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Mobile Tab Bar ───────────────────────────────────────────────────────────
function MobileTabBar({ langCode, pathname }: { langCode: string; pathname: string }) {
  const active = getActiveScreen(pathname);
  const prefix = `/learn-${langCodeToUrlName(langCode)}`;
  const items = [
    { id: 'dashboard', label: 'Home',     Icon: IconDashboard, href: prefix },
    { id: 'practice',  label: 'Practice', Icon: IconPractice,  href: `${prefix}/practice` },
    { id: 'garden',    label: 'Garden',   Icon: IconGarden,    href: `${prefix}/garden` },
    { id: 'progress',  label: 'Progress', Icon: IconProgress,  href: `${prefix}/progress` },
    { id: 'settings',  label: 'Settings', Icon: IconSettings,  href: `${prefix}/settings` },
  ];
  return (
    <div className="c8-mobile-tab-bar">
      {items.map(({ id, label, Icon, href }) => {
        const isActive = active === id;
        return (
          <a key={id} href={href} style={{
            flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
            justifyContent: 'center', gap: 3,
            borderTop: `2px solid ${isActive ? 'var(--accent)' : 'transparent'}`,
            background: isActive ? C.accentBg : 'transparent', textDecoration: 'none',
            color: isActive ? 'var(--accent)' : C.muted,
          }}>
            <Icon size={17} color={isActive ? 'var(--accent)' : C.muted}/>
            <span style={{ fontFamily: AF.b, fontSize: 10, fontWeight: isActive ? 700 : 400 }}>{label}</span>
          </a>
        );
      })}
    </div>
  );
}

// ── App shell (sidebar layout) ───────────────────────────────────────────────
function AppShell({ children }: { children: React.ReactNode }) {
  const pageContext = usePageContext();
  const pathname = pageContext.urlPathname ?? '/';
  const langCode = pageContext.routeParams?.language ?? 'es';

  useProgressSync();
  useGardenSync(langCode);

  // Keep data-lang in sync on client (server already injects it, this handles SPA nav)
  useEffect(() => {
    applyTheme(langCodeToName(langCode));
  }, [langCode]);

  // Navigate to same sub-path under a different language
  function onLanguageChange(code: string) {
    const subpath = pathname.replace(/^\/learn-[^/]+/, '');
    window.location.href = `/learn-${langCodeToUrlName(code)}${subpath}`;
  }

  // Keyboard shortcuts
  useEffect(() => {
    const prefix = `/learn-${langCodeToUrlName(langCode)}`;
    const handler = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.key === 'D') window.location.href = prefix;
      else if (e.key === 'P') window.location.href = `${prefix}/practice`;
      else if (e.key === 'R') window.location.href = `${prefix}/progress`;
      else if (e.key === 'S') window.location.href = `${prefix}/settings`;
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [langCode]);

  return (
    <div className="c8-app-shell">
      <AppSidebar langCode={langCode} pathname={pathname} onLanguageChange={onLanguageChange}/>
      <div className="c8-main">
        <MobileHeader langCode={langCode} onLanguageChange={onLanguageChange}/>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {children}
        </div>
      </div>
      <MobileTabBar langCode={langCode} pathname={pathname}/>
    </div>
  );
}

// ── Marketing shell — renders children directly, pages supply their own chrome ─
function MarketingShell({ children }: { children: React.ReactNode }) {
  return <>{children}</>;
}

// ── Root layout ──────────────────────────────────────────────────────────────
export default function LayoutDefault({ children }: { children: React.ReactNode }) {
  const pageContext = usePageContext();
  const pathname = pageContext.urlPathname ?? '/';

  // App shell for learn/* routes
  const isAppRoute = /^(\/learn-[^/]|\/grammar|\/vocabulary|\/sentences|\/comprehension|\/speaking|\/progress|\/settings|\/pricing|\/cefr-practice|\/tense-practice)/.test(pathname);

  return isAppRoute
    ? <AppShell>{children}</AppShell>
    : <MarketingShell>{children}</MarketingShell>;
}
